// src/components/elements/KanbanBoard/TaskCustomFields.tsx
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Divider,
  Typography,
  Spin,
  message,
  Empty,
  Button
} from 'antd';
import CustomFieldValue from './CustomFieldValue';
import { KanbanTask } from '../../../../stores/KanbanTaskStores';
import { useStore } from '../../../../stores/root-store-context';

const { Title } = Typography;

interface TaskCustomFieldsProps {
  task: KanbanTask;
  t: (key: string) => string;
  isAnonymous?: boolean;
}

const TaskCustomFields: React.FC<TaskCustomFieldsProps> = observer(({ task, t, isAnonymous }) => {
  const { kanbanTaskStore, employeesStore, anonymousStore } = useStore();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load custom field definitions for the project if not already loaded
        if (isAnonymous ? anonymousStore.projectCustomFields.length === 0 : kanbanTaskStore.projectCustomFields.length === 0) {
          isAnonymous ?
            await anonymousStore.getProjectCustomFields(task.projectId.toString()) :
            await kanbanTaskStore.getProjectCustomFields(task.projectId.toString());
        }

        // Load values for the current task
        isAnonymous ?
          await anonymousStore.getTaskCustomFieldValues(task.id.toString()) :
          await kanbanTaskStore.getTaskCustomFieldValues(task.id.toString());
      } catch (error) {
        console.error("Error loading custom fields:", error);
        message.error(t('FailedToLoadCustomFields'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [task.id, task.projectId, kanbanTaskStore, anonymousStore, t]);

  const handleSaveFieldValue = async (fieldId: string, value: string | null): Promise<void> => {
    try {
      const success = isAnonymous ?
        await anonymousStore.setTaskCustomFieldValue(task.id.toString(), fieldId, value || '') :
        await kanbanTaskStore.setTaskCustomFieldValue(task.id.toString(), fieldId, value || '');
      if (success) {
        message.success(t('CustomFieldUpdated'));
      } else {
        message.error(t('FailedToUpdateCustomField'));
      }
    } catch (error) {
      console.error("Error saving custom field value:", error);
      message.error(t('ErrorSavingCustomField'));
    }
  };

  if (loading) {
    return (
      <div
        className="task-custom-fields-loading"
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '20px 0' }}
      >
        <Spin size="small" />
      </div>
    );
  }

  const fields = (isAnonymous ? anonymousStore.projectCustomFields : kanbanTaskStore.projectCustomFields) || [];

  if (fields.length === 0) {
    return <></>;
  }

  // Sort fields by displayOrder
  const sortedFields = [...fields].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div className="task-detail-field custom-fields-section">
      <div className="field-header">
        <Title level={5} className="field-title">{t('CustomFields')}</Title>
      </div>

      <Spin spinning={isAnonymous ? anonymousStore.isCustomFieldsLoading : kanbanTaskStore.isCustomFieldsLoading}>
        <div className="custom-fields-list">
          {sortedFields.map((field) => {
            const fieldValue = isAnonymous ?
              anonymousStore.getFieldValueForTask(task.id.toString(), field.id) :
              kanbanTaskStore.getFieldValueForTask(task.id.toString(), field.id);

            return (
              <div key={field.id} className="custom-field-item">
                <div className="custom-field-name">
                  {field.name}
                  {field.required && <span className="required-mark">*</span>}
                </div>
                <CustomFieldValue
                  field={field}
                  value={fieldValue}
                  onSave={(value) => handleSaveFieldValue(field.id, value)}
                  users={employeesStore.users}
                  disabled={isAnonymous}    
                />
              </div>
            );
          })}
        </div>
      </Spin>
    </div>
  );
});

export default TaskCustomFields;